import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Group as PanelGroup, Panel } from 'react-resizable-panels';
import { Bell, AlertTriangle } from 'lucide-react';
import { useAuth } from '../../components/auth/AuthContext';
import ForwardMaturityGrid from '../../components/grids/ForwardMaturityGrid';
import OurExpiryCalcGrid from '../../components/grids/OurExpiryCalcGrid';
import CptyExpiryCalcGrid from '../../components/grids/CptyExpiryCalcGrid';
import ExpiryExceptionsModal from '../../components/expiry/ExpiryExceptionsModal';
import ExpiryResponseModal from '../../components/expiry/ExpiryResponseModal';
import ExpiryNotificationModal from '../../components/expiry/ExpiryNotificationModal';
import FixingRatesPanel from '../../components/expiry/FixingRatesPanel';
import ResizeHandle from '../../components/common/ResizeHandle';
import Toast from '../../components/common/Toast';
import '../../styles/FilterButtons.css';
import '../../styles/ForwardMaturity.css';
import '../../styles/App.css';
import { clientService } from '../../services/clientService';
import { expiryExceptionsService } from '../../services/api/expiryExceptionsService';
import { useEventStreamSimple } from '../../hooks/useEventStreamSimple';
import SystemDateDisplay from '../../components/common/SystemDateDisplay';
import { useSystemDate } from '../../hooks/useSystemDate';
import { logger } from '../../utils/logger';

// Helper: today's date in YYYY-MM-DD format for the date input and API
const todayISO = (): string => {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

const ForwardMaturity: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const isExpiryActionAllowed = user?.role !== 'client_ro_user' && user?.role !== 'client_fo_user' && user?.role !== 'client_acc_user';

  // Data state
  const [trades, setTrades] = useState<Record<string, any>[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Selection & calculations
  const [selectedTrades, setSelectedTrades] = useState<any[]>([]);
  const [ourCalculations, setOurCalculations] = useState<any[]>([]);
  const [cptyCalculations, setCptyCalculations] = useState<any[]>([]);

  // Scroll sync refs for calculation panels
  const ourCalcScrollRef = useRef<HTMLDivElement>(null);
  const cptyCalcScrollRef = useRef<HTMLDivElement>(null);
  const isScrollSyncing = useRef(false);

  // Filters
  const [selectedDate, setSelectedDate] = useState<string>('');
  const [pendingDate, setPendingDate] = useState<string>(''); // input buffer — committed on OK / Enter
  const [selectedCounterparties, setSelectedCounterparties] = useState<Set<string>>(new Set());
  const [selectedCurrencies, setSelectedCurrencies] = useState<Set<string>>(new Set());
  const [selectedProducts, setSelectedProducts] = useState<Set<string>>(new Set());
  const [selectedSettlementTypes, setSelectedSettlementTypes] = useState<Set<string>>(new Set());

  // Toast notification state
  const [toastVisible, setToastVisible] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState<'success' | 'warning'>('success');

  // Exceptions modal state
  const [showExceptionsModal, setShowExceptionsModal] = useState(false);
  const [exceptionsCount, setExceptionsCount] = useState(0);

  // Expiry response modal state
  const [showExpiryResponseModal, setShowExpiryResponseModal] = useState(false);

  // Expiry notification modal state (proactive — for counterparties that don't send expiries)
  const [showNotificationModal, setShowNotificationModal] = useState(false);

  // Expiry tolerance settings (loaded once for the modal comparison)
  const [expiryTolerances, setExpiryTolerances] = useState<{ currencyTolerances: { currency: string; tolerance: number }[]; defaultTolerance: number } | null>(null);

  // KAN-244: Scroll trigger for non-contract-signed section
  const [scrollToNCStrigger, setScrollToNCStrigger] = useState(0);

  // Derived unique values for filter buttons
  const [uniqueCounterparties, setUniqueCounterparties] = useState<string[]>([]);
  const [uniqueSettlementCurrencies, setUniqueSettlementCurrencies] = useState<string[]>([]);


  // Enrich trades with ProductLabel and settlement amounts
  const enrichedTrades = useMemo(() => {
    return trades.map(trade => {
      const isPhysicalDelivery = trade.SettlementType === 'Entrega Física';

      const base = {
        ...trade,
        ProductLabel: trade.IsForwardStarting ? 'Forward (Starting)'
          : trade.IsAverageRateForward ? 'Forward (Asiático)'
          : 'Forward',
      };

      if (isPhysicalDelivery) {
        // Entrega Física: fixed cashflows from trade data, no fixing needed
        const isBuy = trade.Direction === 'Buy';
        const qty1 = trade.QuantityCurrency1 || 0;
        const price = trade.Price || 0;
        // Use stored QuantityCurrency2 (already rounded at booking). Fall back to
        // calculation only if missing, rounding to 0dp for CLP or 2dp otherwise.
        const currency2Amount = trade.QuantityCurrency2 != null
          ? trade.QuantityCurrency2
          : Number((qty1 * price).toFixed(trade.Currency2 === 'CLP' ? 0 : 2));

        // Forward Starting unfixed: price-dependent amount is unknown
        const isUnfixedStarting = trade.IsForwardStarting && price === 0;

        return {
          ...base,
          DisplayFixingPrice: null,
          DisplayFixingOffset: 'N/A',
          SettlementPrice: null,
          WePayCurrency: isBuy ? trade.Currency2 : trade.Currency1,
          WePayAmount: isBuy ? (isUnfixedStarting ? null : currency2Amount) : qty1,
          WeReceiveCurrency: isBuy ? trade.Currency1 : trade.Currency2,
          WeReceiveAmount: isBuy ? qty1 : (isUnfixedStarting ? null : currency2Amount),
        };
      }

      // Compensación (NDF): fixing-based — amounts come from fixing endpoint
      return {
        ...base,
        DisplayFixingPrice: trade.IsAverageRateForward
          ? (trade.AverageFixingRate ?? trade.FixingRate ?? null)
          : (trade.FixingRate ?? null),
        DisplayFixingOffset: trade.IsAverageRateForward ? 'N/A' : (trade.FixingOffset ?? ''),
      };
    });
  }, [trades]);

  // KAN-244: Separate contract-signed and non-contract-signed trades
  const nonContractSignedCount = useMemo(() => {
    return enrichedTrades.filter(t =>
      t.workflowState === 'Booked' || t.workflowState === 'Confirmed'
    ).length;
  }, [enrichedTrades]);

  // Compose grid data: contract-signed trades first, then separator row, then non-contract-signed
  const composedGridTrades = useMemo(() => {
    const contractSigned = enrichedTrades.filter(t =>
      t.workflowState === 'ContractSigned' || t.workflowState === 'Final'
    );
    const nonContractSigned = enrichedTrades.filter(t =>
      t.workflowState === 'Booked' || t.workflowState === 'Confirmed'
    );

    if (nonContractSigned.length === 0) {
      return contractSigned;
    }

    // Insert separator row between the two groups
    const separatorRow = {
      _isGroupHeader: true,
      _nonContractSignedCount: nonContractSigned.length,
      id: '__separator__',
    };

    return [...contractSigned, separatorRow, ...nonContractSigned];
  }, [enrichedTrades]);

  // Show fixing rates panel whenever there are trades on the selected date
  const hasFixingRateTrades = enrichedTrades.length > 0;

  // Derive unique settlement types from enriched trades
  const uniqueSettlementTypes = useMemo(() => {
    return Array.from(new Set(enrichedTrades.map(t => t.SettlementType).filter(Boolean))).sort();
  }, [enrichedTrades]);

  // Filter settlement currencies based on selected settlement type
  // Hide N/A when only Compensación is selected; hide all when only Entrega Física is selected
  const filteredSettlementCurrencies = useMemo(() => {
    if (selectedSettlementTypes.size === 1 && selectedSettlementTypes.has('Entrega Física')) {
      return []; // No settlement currency filter for physical delivery only
    }
    if (selectedSettlementTypes.size === 1 && selectedSettlementTypes.has('Compensación')) {
      return uniqueSettlementCurrencies.filter(c => c !== 'N/A');
    }
    return uniqueSettlementCurrencies;
  }, [uniqueSettlementCurrencies, selectedSettlementTypes]);

  // Sort selectedTrades by TradeNumber to ensure consistent ordering
  const sortedSelectedTrades = useMemo(() => {
    return [...selectedTrades].sort((a, b) =>
      (a.TradeNumber || '').localeCompare(b.TradeNumber || '')
    );
  }, [selectedTrades]);

  // Stable client ID for API calls (string primitive — won't trigger re-renders like the user object would)
  const clientIdStable = user?.organization?.id;

  // System date — use for date picker default and share with SystemDateDisplay
  const systemDateResult = useSystemDate(clientIdStable);

  // Once system date resolves, set the date picker (only if still at initial empty value)
  const dateInitialized = useRef(false);
  useEffect(() => {
    if (systemDateResult.systemDate && !dateInitialized.current) {
      dateInitialized.current = true;
      setSelectedDate(systemDateResult.systemDate);
      setPendingDate(systemDateResult.systemDate);
    }
  }, [systemDateResult.systemDate]);

  // Fetch data for the selected date
  const fetchData = useCallback(async (date: string, silent = false) => {
    if (!clientIdStable) {
      setError('Client ID not found - user must be logged in');
      setLoading(false);
      return;
    }

    try {
      if (!silent) {
        setLoading(true);
      }
      setError(null);

      // Fetch forward expiries (trade docs include CounterpartySettlement nested object)
      const [tradesResponse, tolResponse] = await Promise.all([
        clientService.getForwardExpiries(clientIdStable, date),
        !expiryTolerances ? clientService.getExpiryTolerances(clientIdStable) : Promise.resolve(null),
      ]);

      if (tradesResponse.success) {
        setTrades(tradesResponse.data);

        // Extract unique filter values
        const counterparties = Array.from(new Set(tradesResponse.data.map(t => t.CounterpartyName).filter(Boolean)));
        const currencies = Array.from(new Set(tradesResponse.data.map(t => t.SettlementCurrency).filter(Boolean)));

        setUniqueCounterparties(counterparties);
        setUniqueSettlementCurrencies(currencies);
      } else {
        setError(tradesResponse.message);
      }

      if (tolResponse?.success && tolResponse.data) {
        setExpiryTolerances(tolResponse.data);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch data');
    } finally {
      setLoading(false);
    }
  }, [clientIdStable]);

  // Initial fetch and re-fetch when date changes (skip if date not yet set)
  useEffect(() => {
    if (selectedDate) fetchData(selectedDate);
  }, [selectedDate, fetchData]);

  // SSE: update individual trade when counterparty expiry email is processed.
  // Show one toast per batch, debounced so rapid events don't spam.
  // seenEventIds: deduplicate toasts across SSE reconnections (same approach as Confirmations page).
  const sseToastRef = useRef<{ counterparty: string; maturityDate: string; matchStatus: string; timeout: ReturnType<typeof setTimeout> | null }>({ counterparty: '', maturityDate: '', matchStatus: '', timeout: null });
  const SEEN_EVENTS_KEY = 'expiry_seen_event_ids';
  const seenEventIds = useRef<Set<string>>(new Set<string>());
  if (seenEventIds.current.size === 0) {
    try {
      const stored = sessionStorage.getItem(SEEN_EVENTS_KEY);
      if (stored) {
        const ids: string[] = JSON.parse(stored);
        ids.forEach(id => seenEventIds.current.add(id));
      }
    } catch { /* ignore corrupt storage */ }
  }

  useEventStreamSimple({
    clientId: clientIdStable,
    eventTypes: ['counterparty_expiry_created', 'expiry_state_updated'],
    onEvent: (eventData) => {
      logger.log('[ForwardMaturity] SSE event received:', eventData.type, eventData.payload);
      const { matchStatus, tradeId, settlementData, maturityDate, counterpartyName } = eventData.payload || {};

      // Deduplicate toasts by event ID — survives SSE reconnections which replay recent events.
      const eventId = eventData.id;
      const alreadySeen = eventId ? seenEventIds.current.has(eventId) : false;
      if (eventId && !alreadySeen) {
        seenEventIds.current.add(eventId);
        try {
          sessionStorage.setItem(SEEN_EVENTS_KEY, JSON.stringify([...seenEventIds.current]));
        } catch { /* storage full — non-critical */ }
      }
      const isReplay = alreadySeen;

      if (matchStatus === 'matched' && tradeId && settlementData) {
        // Always update trade state (replayed or not) so the grid reflects current data
        setTrades(prev => prev.map(t =>
          t.id === tradeId ? { ...t, CounterpartySettlement: settlementData, expiryState: 'received' } : t
        ));
        setSelectedTrades(prev => prev.map(t =>
          t.id === tradeId ? { ...t, CounterpartySettlement: settlementData, expiryState: 'received' } : t
        ));
        if (!isReplay) {
          if (settlementData.CounterpartyName) sseToastRef.current.counterparty = settlementData.CounterpartyName;
          if (maturityDate) sseToastRef.current.maturityDate = maturityDate;
          sseToastRef.current.matchStatus = 'matched';
        }
      }

      if (matchStatus === 'unmatched' || matchStatus === 'processing_failed') {
        // Refresh exceptions badge immediately
        loadExceptionsCount();
        if (!isReplay) {
          if (counterpartyName) sseToastRef.current.counterparty = counterpartyName;
          if (maturityDate) sseToastRef.current.maturityDate = maturityDate;
          sseToastRef.current.matchStatus = matchStatus;
        }
      }

      // Handle expiry_state_updated from automation
      if (eventData.type === 'expiry_state_updated') {
        const stateUpdates = eventData.payload?.stateUpdates as Record<string, string> | undefined;
        if (stateUpdates) {
          setTrades(prev => prev.map(t =>
            stateUpdates[t.id] ? { ...t, expiryState: stateUpdates[t.id] } : t
          ));
          setSelectedTrades(prev => prev.map(t =>
            stateUpdates[t.id] ? { ...t, expiryState: stateUpdates[t.id] } : t
          ));
        }
      }

      // Only show toast for live events, not replays
      if (!isReplay) {
        if (sseToastRef.current.timeout) clearTimeout(sseToastRef.current.timeout);
        sseToastRef.current.timeout = setTimeout(() => {
          sseToastRef.current.timeout = null;
          const { counterparty, maturityDate: md, matchStatus: status } = sseToastRef.current;
          const parts: string[] = [];
          if (counterparty) parts.push(counterparty);
          if (md) parts.push(md);
          const context = parts.length > 0 ? parts.join(', ') : '';

          if (status === 'processing_failed') {
            setToastType('warning');
            setToastMessage(t('forwardMaturity.toastProcessingFailed', { context }));
          } else if (status === 'unmatched') {
            setToastType('warning');
            setToastMessage(t('forwardMaturity.toastUnmatchedExpiry', { context }));
          } else {
            setToastType('success');
            setToastMessage(t('forwardMaturity.toastCounterpartySummary', { context }));
          }
          setToastVisible(true);
          sseToastRef.current.counterparty = '';
          sseToastRef.current.maturityDate = '';
          sseToastRef.current.matchStatus = '';
        }, 500);
      }
    }
  });

  // Load exceptions count for bell badge
  const loadExceptionsCount = useCallback(async () => {
    if (!clientIdStable) return;
    try {
      const result = await expiryExceptionsService.getExceptions(clientIdStable);
      if (result.success && result.data) {
        setExceptionsCount(result.data.recentCount ?? result.data.totalCount ?? 0);
      }
    } catch (err) {
      logger.error('Failed to load expiry exceptions count:', err);
    }
  }, [clientIdStable]);

  useEffect(() => {
    loadExceptionsCount();
  }, [loadExceptionsCount]);

  const handleSelectionChanged = (selectedRows: any[]) => {
    setSelectedTrades(selectedRows);
  };

  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Only update the input buffer — fetch is triggered on OK / Enter
    setPendingDate(e.target.value);
  };

  const handleDateApply = () => {
    if (!pendingDate || pendingDate === selectedDate) return;
    setSelectedDate(pendingDate);
    // Clear selection and filters when date changes
    setSelectedTrades([]);
    setOurCalculations([]);
    setCptyCalculations([]);
    setSelectedCounterparties(new Set());
    setSelectedCurrencies(new Set());
    setSelectedProducts(new Set());
    setSelectedSettlementTypes(new Set());
  };

  const toggleCounterparty = (counterparty: string) => {
    const newSelected = new Set(selectedCounterparties);
    if (newSelected.has(counterparty)) {
      newSelected.delete(counterparty);
    } else {
      newSelected.add(counterparty);
    }
    setSelectedCounterparties(newSelected);
  };

  const toggleCurrency = (currency: string) => {
    const newSelected = new Set(selectedCurrencies);
    if (newSelected.has(currency)) {
      newSelected.delete(currency);
    } else {
      newSelected.add(currency);
    }
    setSelectedCurrencies(newSelected);
  };

  const toggleProduct = (product: string) => {
    const newSelected = new Set(selectedProducts);
    if (newSelected.has(product)) {
      newSelected.delete(product);
    } else {
      newSelected.add(product);
    }
    setSelectedProducts(newSelected);
  };

  const toggleSettlementType = (type: string) => {
    const newSelected = new Set(selectedSettlementTypes);
    if (newSelected.has(type)) {
      newSelected.delete(type);
    } else {
      newSelected.add(type);
    }
    setSelectedSettlementTypes(newSelected);
    // Clear settlement currency selection when settlement type changes
    setSelectedCurrencies(new Set());
  };

  const handleClearFilters = () => {
    setSelectedCounterparties(new Set());
    setSelectedCurrencies(new Set());
    setSelectedProducts(new Set());
    setSelectedSettlementTypes(new Set());
  };

  const handleRefresh = () => {
    fetchData(selectedDate);
  };

  const handleSilentRefresh = useCallback(() => {
    fetchData(selectedDate, true);
  }, [fetchData, selectedDate]);

  const handleOurCalculationsChanged = (calculations: any[]) => {
    setOurCalculations(calculations);
  };

  // Build counterparty calculations from the CounterpartySettlement nested object on each trade
  // NOTE: We don't calculate - we use the settlement amounts received from counterparty
  useEffect(() => {
    const cptyCalcs = sortedSelectedTrades.map(trade => {
      const cptySettlement = trade.CounterpartySettlement;

      if (!cptySettlement) {
        logger.warn(`[ForwardMaturity] No CounterpartySettlement found for trade ${trade.TradeNumber}`);
        return {
          ...trade,
          WePayCurrency: trade.SettlementCurrency,
          WePayAmount: 0,
          WeReceiveCurrency: trade.SettlementCurrency,
          WeReceiveAmount: 0
        };
      }

      // Use counterparty's settlement data directly (no calculation).
      // Reset settlement fields first so enriched trade values don't leak through
      // when the counterparty only provides one side (e.g. WeReceive but not WePay).
      return {
        ...trade,
        WePayCurrency: null,
        WePayAmount: null,
        WeReceiveCurrency: null,
        WeReceiveAmount: null,
        ...cptySettlement,
      };
    });
    setCptyCalculations(cptyCalcs);
  }, [sortedSelectedTrades]);

  // Synchronized scrolling between Our Calculations and Counterparty Calculations panels
  useEffect(() => {
    const ourEl = ourCalcScrollRef.current;
    const cptyEl = cptyCalcScrollRef.current;
    if (!ourEl || !cptyEl) return;

    const handleOurScroll = () => {
      if (isScrollSyncing.current) return;
      isScrollSyncing.current = true;
      cptyEl.scrollTop = ourEl.scrollTop;
      isScrollSyncing.current = false;
    };

    const handleCptyScroll = () => {
      if (isScrollSyncing.current) return;
      isScrollSyncing.current = true;
      ourEl.scrollTop = cptyEl.scrollTop;
      isScrollSyncing.current = false;
    };

    ourEl.addEventListener('scroll', handleOurScroll);
    cptyEl.addEventListener('scroll', handleCptyScroll);
    return () => {
      ourEl.removeEventListener('scroll', handleOurScroll);
      cptyEl.removeEventListener('scroll', handleCptyScroll);
    };
  }, []);

  // Trades eligible for expiry response (expiryState === 'received')
  const receivedTrades = useMemo(() =>
    sortedSelectedTrades.filter(t => t.expiryState === 'received'),
    [sortedSelectedTrades]
  );

  // Trades eligible for proactive notification (expiryState === 'pending' or 'notified')
  const notifiableTrades = useMemo(() =>
    sortedSelectedTrades.filter(t => t.expiryState === 'pending' || t.expiryState === 'notified'),
    [sortedSelectedTrades]
  );

  const handleExpiryResponseSuccess = (agreedIds: string[], disputedIds: string[], siStoragePaths: Record<string, string> = {}) => {
    // Update local state so grid reflects new expiryState + SI storage paths immediately
    const allIds = [...agreedIds, ...disputedIds];
    const updateTrade = (t: any) => {
      if (agreedIds.includes(t.id)) {
        return { ...t, expiryState: 'agreed', ...(siStoragePaths[t.id] ? { settlementInstructionStoragePath: siStoragePaths[t.id] } : {}) };
      }
      if (disputedIds.includes(t.id)) return { ...t, expiryState: 'disputed' };
      return t;
    };
    setTrades(prev => prev.map(updateTrade));
    setSelectedTrades(prev => prev.map(updateTrade));
    setShowExpiryResponseModal(false);
    setToastType('success');
    setToastMessage(t('forwardMaturity.expiryResponse.successSent', { count: allIds.length }));
    setToastVisible(true);
  };

  const handleNotificationSuccess = (notifiedIds: string[], agreedIds: string[], manualAcceptTrades: { tradeId: string; wePayAmount?: number; weReceiveAmount?: number }[] = []) => {
    const manualMap = new Map(manualAcceptTrades.map(m => [m.tradeId, m]));
    const updateTrade = (t: any) => {
      if (agreedIds.includes(t.id)) {
        const manual = manualMap.get(t.id);
        if (manual) {
          // Archive originals and overwrite with counterparty amounts
          return {
            ...t,
            expiryState: 'agreed',
            originalSettlement: {
              WePayAmount: t.WePayAmount,
              WeReceiveAmount: t.WeReceiveAmount,
              WePayCurrency: t.WePayCurrency,
              WeReceiveCurrency: t.WeReceiveCurrency,
            },
            WePayAmount: manual.wePayAmount ?? t.WePayAmount,
            WeReceiveAmount: manual.weReceiveAmount ?? t.WeReceiveAmount,
          };
        }
        return { ...t, expiryState: 'agreed' };
      }
      if (notifiedIds.includes(t.id)) return { ...t, expiryState: 'notified' };
      return t;
    };
    setTrades(prev => prev.map(updateTrade));
    setSelectedTrades(prev => prev.map(updateTrade));
    setShowNotificationModal(false);
    setToastType('success');
    if (notifiedIds.length > 0 && agreedIds.length === 0) {
      setToastMessage(t('forwardMaturity.expiryNotification.successNotified', { count: notifiedIds.length }));
    } else if (agreedIds.length > 0 && notifiedIds.length === 0) {
      setToastMessage(t('forwardMaturity.expiryNotification.successAgreed', { count: agreedIds.length }));
    } else {
      setToastMessage(t('forwardMaturity.expiryNotification.successMixed', { notified: notifiedIds.length, agreed: agreedIds.length }));
    }
    setToastVisible(true);
  };

  return (
    <div className="forward-maturity-page">
      {/* Header */}
      <div className="forward-maturity-header">
        <div className="forward-maturity-title">{t('navigation.forwardExpiries')}</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          {hasFixingRateTrades && clientIdStable && selectedDate && (
            <FixingRatesPanel
              clientId={clientIdStable}
              selectedDate={selectedDate}
              onFixingComplete={handleSilentRefresh}
            />
          )}
          <SystemDateDisplay clientId={clientIdStable} systemDateData={systemDateResult} />
          <button
            className="exceptions-bell-btn"
            onClick={() => setShowExceptionsModal(true)}
            title={t('expiryExceptions.title')}
          >
            <Bell size={20} />
            {exceptionsCount > 0 && (
              <span className="exceptions-badge">{exceptionsCount}</span>
            )}
          </button>
        </div>
      </div>

      {/* Resizable Panel Layout */}
      <PanelGroup
        orientation="vertical"
        className="forward-maturity-panel-group"
      >
        {/* Top Panel - Forward Maturity Grid */}
        <Panel defaultSize={50} minSize={20}>
          <div className="forward-maturity-top-panel">
            <div className="forward-maturity-grid-header">
              <h3>{t('forwardMaturity.gridTitle')}</h3>
              <div className="forward-maturity-filters">
                {/* Date Filter */}
                <div className="filter-group">
                  <label>{t('forwardMaturity.filters.date')}:</label>
                  <input
                    type="date"
                    value={pendingDate}
                    onChange={handleDateChange}
                    onKeyDown={(e) => { if (e.key === 'Enter') handleDateApply(); }}
                    className="date-filter-input"
                  />
                  {pendingDate !== selectedDate && pendingDate && (
                    <button
                      onClick={handleDateApply}
                      style={{
                        background: 'rgba(255,255,255,0.1)',
                        border: '1px solid rgba(255,255,255,0.2)',
                        borderRadius: '4px',
                        color: '#e2e8f0',
                        padding: '2px 10px',
                        fontSize: '12px',
                        height: '28px',
                        cursor: 'pointer',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      OK
                    </button>
                  )}
                  {nonContractSignedCount > 0 && (
                    <button
                      className="exceptions-bell-btn"
                      onClick={() => setScrollToNCStrigger(prev => prev + 1)}
                      title={t('forwardMaturity.nonContractSigned.tooltip', { count: nonContractSignedCount })}
                      style={{ marginLeft: '4px' }}
                    >
                      <AlertTriangle size={18} color="#f59e0b" />
                      <span className="exceptions-badge" style={{ backgroundColor: '#f59e0b' }}>
                        {nonContractSignedCount}
                      </span>
                    </button>
                  )}
                </div>
                {/* Counterparty Filter */}
                <div className="filter-group">
                  <label>{t('forwardMaturity.filters.counterparty')}:</label>
                  <div className="filter-buttons">
                    {uniqueCounterparties.map((cpty) => (
                      <button
                        key={cpty}
                        onClick={() => toggleCounterparty(cpty)}
                        className={`filter-btn ${selectedCounterparties.has(cpty) ? 'active' : ''}`}
                      >
                        {cpty}
                      </button>
                    ))}
                  </div>
                </div>
                {/* Settlement Type Filter */}
                <div className="filter-group">
                  <label>{t('forwardMaturity.filters.settlementType')}:</label>
                  <div className="filter-buttons">
                    {uniqueSettlementTypes.map((type) => (
                      <button
                        key={type}
                        onClick={() => toggleSettlementType(type)}
                        className={`filter-btn ${selectedSettlementTypes.has(type) ? 'active' : ''}`}
                      >
                        {type}
                      </button>
                    ))}
                  </div>
                </div>
                {/* Settlement Currency Filter — hidden when only Entrega Física is selected */}
                {filteredSettlementCurrencies.length > 0 && (
                  <div className="filter-group">
                    <label>{t('forwardMaturity.filters.settlementCurrency')}:</label>
                    <div className="filter-buttons">
                      {filteredSettlementCurrencies.map((curr) => (
                        <button
                          key={curr}
                          onClick={() => toggleCurrency(curr)}
                          className={`filter-btn ${selectedCurrencies.has(curr) ? 'active' : ''}`}
                        >
                          {curr}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                {/* Product Filter */}
                <div className="filter-group">
                  <label>{t('forwardMaturity.filters.product')}:</label>
                  <div className="filter-buttons">
                    <button
                      onClick={() => toggleProduct('Forward')}
                      className={`filter-btn ${selectedProducts.has('Forward') ? 'active' : ''}`}
                    >
                      Forward
                    </button>
                    <button
                      onClick={() => toggleProduct('Forward Asiático')}
                      className={`filter-btn ${selectedProducts.has('Forward Asiático') ? 'active' : ''}`}
                    >
                      Forward Asiático
                    </button>
                    <button
                      onClick={() => toggleProduct('Forward Starting')}
                      className={`filter-btn ${selectedProducts.has('Forward Starting') ? 'active' : ''}`}
                    >
                      Forward Starting
                    </button>
                  </div>
                </div>
                <button
                  onClick={handleClearFilters}
                  className="action-btn clear-btn"
                >
                  {t('forwardMaturity.filters.clearFilters')}
                </button>
                <button
                  onClick={handleRefresh}
                  disabled={loading}
                  className={`action-btn refresh-btn ${loading ? 'loading' : ''}`}
                >
                  {loading ? t('forwardMaturity.filters.refreshing') : t('forwardMaturity.filters.refresh')}
                </button>
              </div>
            </div>
            <div className="forward-maturity-grid-container">
              {loading ? (
                <div className="grid-loading">
                  <p>{t('forwardMaturity.loading')}</p>
                </div>
              ) : error ? (
                <div className="grid-error">
                  <p>Error: {error}</p>
                </div>
              ) : (
                <ForwardMaturityGrid
                  trades={composedGridTrades}
                  onSelectionChanged={handleSelectionChanged}
                  selectedCounterparties={selectedCounterparties}
                  selectedCurrencies={selectedCurrencies}
                  selectedProducts={selectedProducts}
                  selectedSettlementTypes={selectedSettlementTypes}
                  clientId={clientIdStable}
                  onRefresh={handleSilentRefresh}
                  scrollToNonContractSigned={scrollToNCStrigger}
                />
              )}
            </div>
          </div>
        </Panel>

        <ResizeHandle direction="vertical" />

        {/* Bottom Panel - Calculation Sections */}
        <Panel defaultSize={50} minSize={20} style={{ display: 'flex', flexDirection: 'column' }}>
          {/* Action toolbar — visible when received or notifiable trades are selected */}
          {(receivedTrades.length > 0 || notifiableTrades.length > 0) && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem',
              padding: '0.4rem 0.75rem',
              borderBottom: '1px solid var(--border-color)',
              backgroundColor: 'rgba(255,255,255,0.02)',
            }}>
              {receivedTrades.length > 0 && isExpiryActionAllowed && (
                <button
                  onClick={() => setShowExpiryResponseModal(true)}
                  style={{
                    padding: '0.3rem 0.75rem',
                    backgroundColor: 'var(--accent-blue)',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '13px',
                    fontWeight: 600,
                  }}
                >
                  {t('forwardMaturity.expiryResponse.respond')} ({receivedTrades.length})
                </button>
              )}
              {notifiableTrades.length > 0 && isExpiryActionAllowed && (
                <button
                  onClick={() => setShowNotificationModal(true)}
                  style={{
                    padding: '0.3rem 0.75rem',
                    backgroundColor: '#e67e00',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '13px',
                    fontWeight: 600,
                  }}
                >
                  {notifiableTrades.every(t => t.expiryState === 'notified')
                    ? t('forwardMaturity.expiryNotification.notifications')
                    : t('forwardMaturity.expiryNotification.sendNotification')
                  } ({notifiableTrades.length})
                </button>
              )}
            </div>
          )}
          <div className="forward-maturity-bottom-panels">
            <div className="calculation-panel">
              <h3>{t('forwardMaturity.ourCalculations')}</h3>
              <div className="calculation-grid-container" ref={ourCalcScrollRef}>
                <OurExpiryCalcGrid
                  selectedTrades={sortedSelectedTrades}
                  onCalculationsChanged={handleOurCalculationsChanged}
                  cptyCalculations={cptyCalculations}
                  toleranceSettings={expiryTolerances}
                />
              </div>
            </div>
            <div className="calculation-panel">
              <h3>{t('forwardMaturity.counterpartyCalculations')}</h3>
              <div className="calculation-grid-container" ref={cptyCalcScrollRef}>
                <CptyExpiryCalcGrid
                  selectedTrades={cptyCalculations}
                  ourCalculations={ourCalculations}
                  toleranceSettings={expiryTolerances}
                />
              </div>
            </div>
          </div>
        </Panel>
      </PanelGroup>

      <Toast
        message={toastMessage}
        type={toastType}
        isVisible={toastVisible}
        onClose={() => setToastVisible(false)}
        duration={5000}
      />

      {/* Expiry Exceptions Modal */}
      {clientIdStable && (
        <ExpiryExceptionsModal
          clientId={clientIdStable}
          isOpen={showExceptionsModal}
          selectedDate={selectedDate}
          onClose={() => {
            setShowExceptionsModal(false);
            loadExceptionsCount();
          }}
          onRefreshNeeded={() => {
            fetchData(selectedDate);
            loadExceptionsCount();
          }}
        />
      )}

      {/* Expiry Response Modal */}
      {clientIdStable && showExpiryResponseModal && (
        <ExpiryResponseModal
          isOpen={showExpiryResponseModal}
          trades={receivedTrades}
          ourCalculations={ourCalculations}
          cptyCalculations={cptyCalculations}
          clientId={clientIdStable}
          toleranceSettings={expiryTolerances}
          onClose={() => setShowExpiryResponseModal(false)}
          onSuccess={handleExpiryResponseSuccess}
        />
      )}

      {/* Expiry Notification Modal (proactive) */}
      {clientIdStable && showNotificationModal && (
        <ExpiryNotificationModal
          isOpen={showNotificationModal}
          trades={notifiableTrades}
          ourCalculations={ourCalculations}
          clientId={clientIdStable}
          onClose={() => setShowNotificationModal(false)}
          onSuccess={handleNotificationSuccess}
        />
      )}
    </div>
  );
};

export default ForwardMaturity;
